import React from "react";

import CountUp from "react-countup";

import { useInView } from "react-intersection-observer";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn } from "../variants";

import { useEffect, useState, useRef } from "react";

import "./About.css";

import Imageborder from "../assets/aboutboarded.png";
import Imageempty from "../assets/aboutempty.png";
import USCicon from "../assets/TheSeal_Reg_0921.png";
import USCiconDark from "../assets/output-onlinepngtools.png";

const About = ({ isDarkMode }) => {
  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  const [scrollDirection, setScrollDirection] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showMountains, setShowMountains] = useState(false);
  const mountainList = [
    "Fuji",
    "Mt. Baldy",
    "Huangshan",
    "Rainier",
    "Half Dome",
    "San Gorgonio",
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setScrollDirection("down");
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection("up");
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 4; // 拖动速度因子
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  useEffect(() => {
    if (showMountains && scrollRef.current) {
      setTimeout(() => {
        const container = scrollRef.current;
        const firstItem = container.querySelector("li");
        if (firstItem) {
          const containerWidth = container.offsetWidth;
          const itemWidth = firstItem.offsetWidth;
          container.scrollTo({
            left: firstItem.offsetLeft - (containerWidth - itemWidth) / 2,
            behavior: "smooth",
          });
        }
      }, 100); // 等动画挂载后再执行
    }
  }, [showMountains]);

  const scrollByAmount = 320; // 每次滚动的距离

  const handleScrollLeft = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: -scrollByAmount, behavior: "smooth" });
    }
  };

  const handleScrollRight = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: scrollByAmount, behavior: "smooth" });
    }
  };

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMoveContainer = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const width = rect.width;
    const zone = x < width * 0.3 ? "left" : x > width * 0.7 ? "right" : null;

    setMousePos({ x, y, zone });
  };

  return (
    <section // 全局控制字体颜色
      className="dark:text-white text-black flex items-center min-h-[100vh] lg:min-h-[100vh] py-8 relative mx-auto"
      id="about"
      ref={ref}
    >
      <div className="max-w-[1280px] w-full px-4 mx-auto mt-12 ">
        <div className="flex flex-col gap-y-10 lg:flex-row items-center lg:items-start">
          {/* 图片部分：左侧 */}
          <div className="hidden lg:flex lg:w-1/2 justify-center">
            <div className="card">
              <div className="wrapper">
                <img
                  src={Imageborder}
                  alt=""
                  className="cover-image rounded-[20px]"
                />
              </div>
              <img src={Imageempty} alt="" className="character" />
            </div>
          </div>

          {/* 文字部分：右侧 */}
          <div className="hidden lg:flex flex-col gap-y-3 text-center lg:text-left lg:w-1/2">
            <motion.span
              variants={fadeIn(scrollDirection, 0.3)}
              initial="hidden"
              whileInView={"show"}
              viewport={{ once: false, amount: 0.7 }}
              className="text-8xl lg:text-[100px] font-bold whitespace-normal break-words max-w-full"
            >
              I am
            </motion.span>
            <motion.p
              variants={fadeIn(scrollDirection, 0.3)}
              initial="hidden"
              whileInView={"show"}
              viewport={{ once: false, amount: 0.7 }}
              className="text-lg lg:text-xl max-w-3xl text-gray-600 dark:text-gray-400"
            ></motion.p>
            <div className="text-2xl lg:text-3xl" style={{ lineHeight: "1.5" }}>
              <motion.div
                variants={fadeIn("left", 0.3)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, amount: 0.7 }}
              >
                <span className="font-bold">
                  A Master of Computer Science
                  <span className="AItext"> [ AI ] </span>
                  student at{" "}
                  <button
                    className="USCtext bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient-flow justify-center flex flex-row items-center"
                    onClick={() =>
                      window.open("https://www.usc.edu/", "_blank")
                    }
                  >
                    <p className="text-[50px] h-full w-full text-center x-wrap">
                      University of Southern California
                    </p>
                    {isDarkMode ? (
                      <img
                        src={USCiconDark}
                        alt="USC logo"
                        className="w-[100px] h-[100px] object-contain"
                      />
                    ) : (
                      <img
                        src={USCicon}
                        alt="USC logo"
                        className="w-[100px] h-[100px] object-contain"
                      />
                    )}
                  </button>
                </span>
              </motion.div>

              <motion.span
                variants={fadeIn(scrollDirection, 0.5)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, amount: 0.7 }}
              >
                <div className="text-6xl"> FOCUS ON </div>

                <div className="outline-stack mb-2">
                  <span className="outline-layer layer-3 dark:outline-layer-dark">
                    Artificial Intelligence
                  </span>
                  <span className="outline-layer layer-2 dark:outline-layer-dark">
                    Artificial Intelligence
                  </span>
                  <span className="outline-layer layer-1 dark:outline-layer-dark">
                    Artificial Intelligence
                  </span>
                  <span className="solid-text dark:text-green-500">
                    Artificial Intelligence
                  </span>
                </div>

                <div className="outline-stack mb-2">
                  <span className="outline-layer layer-3 dark:outline-layer-dark">
                    Machine Learning
                  </span>
                  <span className="outline-layer layer-2 dark:outline-layer-dark">
                    Machine Learning
                  </span>
                  <span className="outline-layer layer-1 dark:outline-layer-dark">
                    Machine Learning
                  </span>
                  <span className="solid-text dark:text-green-500">
                    Machine Learning
                  </span>
                </div>
              </motion.span>
            </div>
          </div>
        </div>

        {/* ✅ 小屏显示的简化内容 */}
        <div className="lg:hidden px-4 text-center">
          <p className="text-[100px] font-bold mb-2">I am</p>
          <p className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-4">
            A Master of Computer Science [ AI ] student at
          </p>
          <p
            className="text-2xl text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 animate-gradient-flow font-bold"
            onClick={() => window.open("https://www.usc.edu/", "_blank")}
          >
            University of Southern California
          </p>

          <div className="mt-6">
            <p className="text-2xl font-bold">FOCUS ON</p>
            <div className="text-[50px] font-semibold mt-2 ">
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 animate-gradient-flow font-bold dark:text-green-500 mb-4">
                Artificial Intelligence
              </p>
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 animate-gradient-flow font-bold dark:text-green-500">
                Machine Learning
              </p>
            </div>
          </div>
        </div>

        <div className="relative mt-[80px] w-full flex justify-center px-8 italic text-gray-800 dark:text-gray-500">
          <motion.p
            variants={fadeIn(scrollDirection, 0.5)}
            initial="hidden"
            whileInView={"show"}
            viewport={{ once: false, amount: 0.5 }}
            className="text-xl text-center mb-5  mx-auto"
          >
            "For me, learning is a lifelong journey. I believe in curiosity,
            craftsmanship, and using technology and creativity to make a
            positive impact."
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default About;
